import React from 'react';
import { Link } from 'react-router-dom';

const WealthTrackerPrivacy = () => {
  return (
    <div className="legal-page">
      <div className="legal-page__header">
        <div className="container">
          <Link to="/" className="legal-page__back">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5" />
              <path d="M12 19l-7-7 7-7" />
            </svg>
            Back to Home
          </Link>
          <span className="section__tag">Legal</span>
          <h1 className="legal-page__title">Privacy Policy</h1>
          <p className="legal-page__subtitle">
            Wealth Tracker - Net Worth by Wolf Technologies Ltd
          </p>
          <p className="legal-page__date">Effective date: 14 April 2026</p>
        </div>
      </div>

      <div className="container">
        <div className="legal-page__content">

          <section className="legal-section">
            <h2>1. Introduction</h2>
            <p>
              Wolf Technologies Ltd ("we", "us", or "our") operates the Wealth Tracker - Net Worth
              mobile application (the "App"). This Privacy Policy explains how we collect, use, store,
              and protect your personal information when you use our App.
            </p>
            <p>
              By using Wealth Tracker, you agree to the collection and use of information in accordance
              with this policy. If you do not agree with this policy, please do not use the App.
            </p>
          </section>

          <section className="legal-section">
            <h2>2. Data We Collect</h2>
            <p>We collect the following types of data to provide and improve the App:</p>

            <h3>Account Information</h3>
            <ul>
              <li>Email address (for account login and authentication)</li>
            </ul>

            <h3>Financial Holdings</h3>
            <ul>
              <li>Asset and liability names (e.g. "ISA", "Mortgage")</li>
              <li>Values and currencies</li>
              <li>Categories and types</li>
            </ul>

            <h3>Net Worth Data</h3>
            <ul>
              <li>Snapshots of your net worth over time</li>
              <li>Financial goals and targets</li>
            </ul>

            <h3>Sharing Data</h3>
            <ul>
              <li>Email addresses of people you invite to view your portfolio</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>3. Data We Do NOT Collect</h2>
            <p>We want to be transparent about what we do not collect:</p>
            <ul>
              <li>
                <strong>Bank account numbers or credentials</strong> — We never ask for or store your
                banking login details, sort codes, or account numbers.
              </li>
              <li>
                <strong>Transaction history</strong> — We do not connect to your bank or import transactions.
              </li>
              <li><strong>Location data</strong></li>
              <li><strong>Photos or contacts</strong></li>
              <li>
                <strong>Payment card details</strong> — All payments are handled directly by Apple
                (In-App Purchase) or Stripe. We do not store your payment card details.
              </li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>4. How We Use Your Data</h2>
            <p>We use the data we collect to:</p>
            <ul>
              <li>Provide and maintain the App's core net worth tracking features</li>
              <li>Authenticate your account and manage login sessions</li>
              <li>Calculate and display your net worth, allocation, and history</li>
              <li>Enable sharing of portfolio data with people you invite</li>
              <li>Improve the App and develop new features</li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>5. Data Storage & Security</h2>
            <p>
              All data is stored securely on AWS infrastructure in the EU, specifically in the
              London region (eu-west-2). Data is encrypted both in transit (TLS) and at rest.
            </p>
            <p>
              We implement industry-standard security measures to protect your data, including
              secure authentication via AWS Cognito and encrypted database storage via AWS DynamoDB.
            </p>
          </section>

          <section className="legal-section">
            <h2>6. Third-Party Services</h2>
            <p>We use the following third-party services to operate the App:</p>
            <ul>
              <li><strong>AWS Cognito</strong> — User authentication and account management</li>
              <li><strong>AWS DynamoDB</strong> — Secure data storage</li>
              <li><strong>Stripe</strong> — Web payment processing</li>
              <li><strong>Apple In-App Purchase</strong> — iOS payment processing</li>
            </ul>
            <p>
              We do not sell or share your data with advertisers. We do not use any third-party
              analytics SDKs. Your data is only shared with the services listed above as necessary
              to provide the App's functionality.
            </p>
          </section>

          <section className="legal-section">
            <h2>7. Data Sharing</h2>
            <p>
              Your financial data is private by default. You may optionally choose to share data by:
            </p>
            <ul>
              <li>
                <strong>Inviting others</strong> — You can invite other users (by email) to view
                your portfolio. Only people you explicitly invite will have access.
              </li>
            </ul>
            <p>
              Data is only ever shared with people you explicitly invite or grant access to.
              We never share your data with third parties for marketing or advertising purposes.
            </p>
          </section>

          <section className="legal-section">
            <h2>8. Data Retention & Deletion</h2>
            <p>
              Your data is retained for as long as your account is active. You have full control
              over your data:
            </p>
            <ul>
              <li>
                <strong>Delete individual holdings</strong> — You can delete any holding at any time.
              </li>
              <li>
                <strong>Export your data</strong> — You can export all your data from the Settings page.
              </li>
              <li>
                <strong>Delete your account</strong> — You can request complete account deletion,
                which will remove all your data from our systems.
              </li>
            </ul>
          </section>

          <section className="legal-section">
            <h2>9. Your Rights</h2>
            <p>Under applicable data protection laws, you have the right to:</p>
            <ul>
              <li>Access the personal data we hold about you</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Object to or restrict processing of your data</li>
              <li>Request a portable copy of your data</li>
            </ul>
            <p>
              To exercise any of these rights, please contact us at the email address below.
            </p>
          </section>

          <section className="legal-section">
            <h2>10. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any
              changes by posting the new Privacy Policy within the App and updating the
              "Effective date" at the top of this page.
            </p>
          </section>

          <section className="legal-section">
            <h2>11. Contact Us</h2>
            <p>
              If you have any questions about this Privacy Policy or your data, please contact us:
            </p>
            <div className="legal-contact">
              <p><strong>Wolf Technologies Ltd</strong></p>
              <p>United Kingdom</p>
              <p>
                Email:{' '}
                <a href="mailto:support@wolftechnologies.co.uk">support@wolftechnologies.co.uk</a>
              </p>
            </div>
          </section>

        </div>
      </div>
    </div>
  );
};

export default WealthTrackerPrivacy;
